import { z } from 'zod';
import { SignalLinkedFindingPreviewSchema } from '@terra/shared-contracts-findings';
import {
  PaginationQueryParamsSchema,
  OptionalBooleanQueryParamSchema,
  OptionalStringArrayQueryParamSchema,
  createConfidenceFilterQueryParamSchema,
  LifecycleStateFilterSchema,
} from '@terra/shared-dto-types';
import {
  BusinessRiskCountSchema,
  EMPTY_BUSINESS_RISK_COUNTS,
  EMPTY_INFRA_IMPACT_COUNTS,
  InfraImpactCountSchema,
  ThreatActorFlowRowSchema,
  WeaknessCountRowSchema,
} from './infra-pov.schema';
import { ALLOWED_REMOTE_EXECUTION_COMMANDS } from './internal-network-c2.constants';
import { InternalNetworkAdEntityDetailSchema } from './internal-network-ad-entity-list.schema';
import {
  GuardrailRuleSchema,
  SurfaceFindingsCountQuerySchema,
  SurfaceFindingsCountResponseSchema,
  SurfaceFindingsListQuerySchema,
  SurfaceFindingsListResponseSchema,
  ExternalNetworkSignalListResponseSchema,
} from '@terra/shared-contracts-external-network';
import {
  AutomationScheduleSchema,
  InputJsonValueSchema,
  InternalDiscoveryRunStatusSchema,
  InternalNetworkActionClassSchema,
  InternalNetworkAdEntityKindSchema,
  InternalNetworkAgentStatusSchema,
  InternalNetworkExecutionTierSchema,
  InternalNetworkOperationStatusSchema,
  InternalNetworkOperationTaskStatusSchema,
  JsonValueSchema,
  ScenarioStatusSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  SignalCategorySchema,
  SignalOriginSchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';

export {
  AutomationScheduleSchema,
  InputJsonValueSchema,
  InternalDiscoveryRunStatusSchema,
  InternalNetworkActionClassSchema,
  InternalNetworkAdEntityKindSchema,
  InternalNetworkAgentStatusSchema,
  InternalNetworkExecutionTierSchema,
  InternalNetworkOperationStatusSchema,
  InternalNetworkOperationTaskStatusSchema,
  JsonValueSchema,
  ScenarioStatusSchema,
};

export type InternalNetworkAdEntityKind = z.infer<
  typeof InternalNetworkAdEntityKindSchema
>;

export const InternalDiscoveryComplianceAuthorizationSchema = z.object({
  actorId: z.string(),
  authorizedAt: z.iso.datetime(),
  scopeSnapshot: z.object({
    networkId: z.string(),
    surfaceName: z.string(),
    domainNames: z.array(z.string()),
    dcIps: z.array(z.string()),
    dnsServerIps: z.array(z.string()),
    executionTier: InternalNetworkExecutionTierSchema,
    signalCategoriesInScope: z.array(SignalCategorySchema),
  }),
});

export const InternalNetworkHostAgentPackagingSchema = z.enum(['WRAPPED']);

export const InternalNetworkEdrAssessmentModeSchema = z.enum([
  'COVERAGE',
  'EFFECTIVENESS',
]);

export const InternalNetworkEdrOutcomeSchema = z.enum([
  'BLOCKED',
  'ALERTED',
  'PERMITTED',
  'UNKNOWN',
]);

export const InternalNetworkSchema = z.object({
  id: z.string(),
  name: z.string(),
  domainNames: z.array(z.string()),
  dcIps: z.array(z.string()),
  dnsServerIps: z.array(z.string()),
  executionTier: InternalNetworkExecutionTierSchema,
  signalCategoriesInScope: z.array(SignalCategorySchema).default([]),
  dataRetentionDays: z.int().positive().nullable(),
  schedule: AutomationScheduleSchema.nullable().optional(),
  smokeTestEnabled: z.boolean().default(false),
  executionSchedule: AutomationScheduleSchema.nullable().optional(),
  isEnabled: z.boolean(),
  smbPipeName: z.string().min(1).nullable().optional(),
  tcpP2pPort: z.int().min(1).max(65535).nullable().optional(),
  hostAgentPackaging:
    InternalNetworkHostAgentPackagingSchema.default('WRAPPED'),
  edrAssessmentMode:
    InternalNetworkEdrAssessmentModeSchema.default('EFFECTIVENESS'),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateInternalNetworkSchema = z.object({
  name: z.string().min(1),
  domainNames: z.array(z.string()).min(1),
  dcIps: z.array(z.string()).default([]),
  dnsServerIps: z.array(z.string()).default([]),
  executionTier: InternalNetworkExecutionTierSchema.default('VALIDATION_ONLY'),
  signalCategoriesInScope: z.array(SignalCategorySchema).default([]),
  schedule: AutomationScheduleSchema.optional(),
  smokeTestEnabled: z.boolean().optional(),
  executionSchedule: AutomationScheduleSchema.optional(),
  dataRetentionDays: z.int().positive().optional(),
  isEnabled: z.boolean().default(true),
  smbPipeName: z.string().min(1).optional(),
  tcpP2pPort: z.int().min(1).max(65535).optional(),
  edrAssessmentMode: InternalNetworkEdrAssessmentModeSchema.optional(),
});

export const UpdateInternalNetworkSchema = z.object({
  name: z.string().min(1).optional(),
  domainNames: z.array(z.string()).min(1).optional(),
  dcIps: z.array(z.string()).optional(),
  dnsServerIps: z.array(z.string()).optional(),
  executionTier: InternalNetworkExecutionTierSchema.optional(),
  signalCategoriesInScope: z.array(SignalCategorySchema).optional(),
  schedule: AutomationScheduleSchema.nullable().optional(),
  smokeTestEnabled: z.boolean().optional(),
  executionSchedule: AutomationScheduleSchema.nullable().optional(),
  dataRetentionDays: z.int().positive().nullable().optional(),
  isEnabled: z.boolean().optional(),
  smbPipeName: z.string().min(1).nullable().optional(),
  tcpP2pPort: z.int().min(1).max(65535).nullable().optional(),
  edrAssessmentMode: InternalNetworkEdrAssessmentModeSchema.optional(),
});

export const InternalNetworkListQuerySchema =
  PaginationQueryParamsSchema.extend({
    isEnabled: OptionalBooleanQueryParamSchema,
  });

export const InternalNetworkListResponseSchema = z.object({
  data: z.array(InternalNetworkSchema),
  total: z.number(),
});

export const InternalNetworkRemoteExecutionStatusSchema = z.enum([
  'NOT_REGISTERED',
  'READY',
  'DENIED',
  'STALE',
  'ERROR',
]);

export const InternalNetworkSensorC2PhaseSchema = z.enum([
  'CHECKING_IN',
  'REGISTERING_CALLBACK',
  'READY',
  'DENIED',
  'ERROR',
]);

export const InternalNetworkDiscoveryBlockReasonSchema = z.enum([
  'no_enrollment',
  'sensor_not_online',
  'sensor_not_bound',
  'sensor_binding_stale',
  'remote_c2_not_provisioned',
  'no_credentials',
  'no_owned_credentials',
  'c2_paused',
  'no_tunnel_enrollment',
  'tunnel_not_online',
  'tunnel_routes_not_approved',
]);

export const InternalNetworkDiscoveryConnectionModeSchema = z.enum([
  'tunnel',
  'sensor',
]);

export const InternalNetworkDiscoveryReadinessSchema = z.object({
  ready: z.boolean(),
  blockReason: InternalNetworkDiscoveryBlockReasonSchema.nullable(),
  connectionMode: InternalNetworkDiscoveryConnectionModeSchema,
  remoteExecutionConfigured: z.boolean(),
  onlineEnrollmentCount: z.number(),
  sensorBoundEnrollmentCount: z.number(),
});

export const InternalNetworkAgentEnrollmentSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  status: InternalNetworkAgentStatusSchema,
  accessKeyId: z.string().nullable(),
  deploymentStatus: z.enum(['PENDING', 'ONLINE', 'OFFLINE', 'REVOKED']),
  remoteExecutionStatus: InternalNetworkRemoteExecutionStatusSchema,
  sensorBound: z.boolean(),
  lastHeartbeatAt: z.iso.datetime().nullable(),
  version: z.string().nullable(),
  imageDigest: z.string().nullable(),
  revokeInProgress: z.boolean().optional(),
  autoUpdateEnabled: z.boolean(),
  releaseCompatibility: z
    .object({
      compatible: z.boolean(),
      message: z.string().optional(),
      evaluatedAt: z.iso.datetime().nullable(),
    })
    .nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const InternalNetworkAgentEnrollmentListResponseSchema = z.object({
  data: z.array(InternalNetworkAgentEnrollmentSchema),
  total: z.number(),
});

export const InternalNetworkSensorReleaseSchema = z.object({
  id: z.string(),
  version: z.string(),
  imageReference: z.string(),
  imageDigest: z.string().nullable(),
  manifestUrl: z.string().nullable(),
  signature: z.string().nullable(),
  sbomUrl: z.string().nullable(),
  composeArtifactKey: z.string().nullable(),
  composeSecretsArtifactKey: z.string().nullable(),
  minimumBackendVersion: z.string().nullable(),
  supportedDeploymentModes: z.array(z.string()),
  releaseChannel: z.string(),
  releaseNotes: z.string().nullable(),
  isLatest: z.boolean(),
  isRevoked: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateInternalNetworkSensorReleaseSchema = z.object({
  version: z.string().min(1),
  imageReference: z.string().min(1),
  imageDigest: z.string().optional(),
  manifestUrl: z.string().min(1).optional(),
  signature: z.string().optional(),
  sbomUrl: z.string().min(1).optional(),
  composeArtifactKey: z.string().optional(),
  composeSecretsArtifactKey: z.string().optional(),
  minimumBackendVersion: z.string().optional(),
  supportedDeploymentModes: z
    .array(z.string().min(1))
    .default([
      'sensor-only-env',
      'sensor-only-secrets',
      'sensor-tunnel-env',
      'sensor-tunnel-secrets',
    ]),
  releaseChannel: z.string().default('stable'),
  releaseNotes: z.string().optional(),
  isLatest: z.boolean().default(true),
});

export const SensorReleaseDownloadBundleSchema = z.object({
  version: z.string(),
  composeDownloadUrl: z.url().nullable(),
  composeSecretsDownloadUrl: z.url().nullable(),
  manifestDownloadUrl: z.url().nullable(),
  sbomDownloadUrl: z.url().nullable(),
  signatureDownloadUrl: z.url().nullable(),
});

export const InternalNetworkC2KillSwitchStatusSchema = z.object({
  enabled: z.boolean(),
  enabledAt: z.iso.datetime().nullable(),
  enabledBy: z.string().nullable(),
  reason: z.string().nullable(),
});

export const EnableInternalNetworkC2KillSwitchSchema = z.object({
  reason: z.string().optional(),
});

export const RotateEnrollmentKeyResponseSchema = z.object({
  enrollment: InternalNetworkAgentEnrollmentSchema,
  apiKey: z.string(),
  deploymentCommand: z.string(),
  composeYaml: z.string(),
  secretsDeploymentCommand: z.string(),
  secretsComposeYaml: z.string(),
  secretsSetupCommand: z.string(),
  imageVerifyScript: z.string(),
  cosignPublicKeyPem: z.string().nullable(),
  pinnedImageReference: z.string(),
});

export const SensorDesiredImageSchema = z.object({
  reference: z.string(),
  digest: z.string().nullable(),
  cosignKeyId: z.string().nullable(),
});

export const AgentHeartbeatResponseSchema = z.object({
  ok: z.literal(true),
  stopDiscoveryRunIds: z.array(z.string()),
  denyRemoteExecution: z.boolean(),
  releaseCompatibility: z.object({
    compatible: z.boolean(),
    message: z.string().optional(),
  }),
  desiredImage: SensorDesiredImageSchema.nullable(),
  autoUpdate: z.boolean(),
});

export const InternalNetworkEvidenceTypeSchema = z.enum([
  'BLOODHOUND_ARCHIVE',
  'COMMAND_OUTPUT',
  'EVIDENCE',
  'GRAPH_EXPORT',
]);

export const CreateOperationEvidenceUploadSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.string().default('application/json'),
  evidenceType: InternalNetworkEvidenceTypeSchema.default('COMMAND_OUTPUT'),
});

export const OperationEvidenceUploadResponseSchema = z.object({
  uploadUrl: z.url(),
  s3Key: z.string(),
});

export const ConfirmOperationEvidenceUploadSchema = z.object({
  s3Key: z.string().min(1),
  contentType: z.string().min(1),
  evidenceType: InternalNetworkEvidenceTypeSchema,
  sizeBytes: z.int().positive().optional(),
  checksum: z.string().optional(),
});

export const InternalEvidenceSchema = z.object({
  id: z.string(),
  operationId: z.string(),
  networkId: z.string(),
  evidenceType: InternalNetworkEvidenceTypeSchema,
  storageKey: z.string(),
  contentType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  createdAt: z.iso.datetime(),
});

export const InternalEvidenceContentSchema = z.object({
  command: z.string(),
  summary: z.string().nullable(),
  actionContent: z.string(),
  resultContent: z.string(),
  evidenceType: InternalNetworkEvidenceTypeSchema,
});

export const BloodhoundCollectionMethodSchema = z.enum([
  'All',
  'Default',
  'DCOnly',
]);

export const BloodhoundCollectionProfileSchema = z.enum([
  'full',
  'default',
  'dc_only',
]);

export const RemoteDiscoveryCollectParamsSchema = z.object({
  domain_name: z.string().min(1),
  dc_ips: z.string().min(1),
  dns_server_ips: z.string().optional(),
  dc_hostname: z.string().optional(),
  collection_method: z
    .union([
      BloodhoundCollectionMethodSchema,
      BloodhoundCollectionProfileSchema,
      z.string(),
    ])
    .optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  ntlm_hash: z.string().optional(),
  auth_method: z.enum(['AUTO', 'NTLM', 'KERBEROS']).optional(),
  discovery_run_id: z.string().optional(),
  reuse_product_discovery: z.boolean().optional(),
});

export const RemoteCredentialVerifyParamsSchema = z.object({
  domain_name: z.string().min(1),
  dc_ips: z.string().min(1),
  dns_server_ips: z.string().optional(),
  username: z.string().min(1),
  password: z.string().optional(),
  ntlm_hash: z.string().optional(),
  auth_method: z.enum(['AUTO', 'NTLM', 'KERBEROS']).optional(),
  credential_type: z
    .enum(['DOMAIN_USER', 'SERVICE_ACCOUNT', 'NTLM_HASH'])
    .optional(),
  credential_id: z.string().optional(),
  discovery_run_id: z.string().optional(),
});

export const InternalNetworkSensorReleaseListResponseSchema = z.object({
  data: z.array(InternalNetworkSensorReleaseSchema),
  total: z.number(),
});

export const InternalNetworkOutboundEndpointSchema = z.object({
  id: z.enum([
    'terra_api',
    'c2_callbacks',
    'container_registry',
    'headscale_control',
    'headscale_derp_stun',
    'subnet_router_registry',
  ]),
  description: z.string(),
  host: z.string(),
  port: z.number(),
  protocol: z.enum(['http', 'https', 'udp']),
  paths: z.array(z.string()),
});

export const InternalNetworkEffectiveSensorReleaseSchema = z.object({
  version: z.string(),
  imageReference: z.string(),
  imageDigest: z.string().nullable(),
});

export const InternalNetworkDeploymentRequirementsSchema = z.object({
  gatewayBaseUrl: z.url(),
  outboundEndpoints: z.array(InternalNetworkOutboundEndpointSchema),
  remoteAttackExecutionEnabled: z.boolean(),
  effectiveSensorRelease: InternalNetworkEffectiveSensorReleaseSchema,
  supportedDeploymentModes: z.array(z.string()),
});

export const InternalNetworkSensorDeploymentArtifactsSchema = z.object({
  deploymentCommand: z.string(),
  composeYaml: z.string(),
  secretsDeploymentCommand: z.string(),
  secretsComposeYaml: z.string(),
  secretsSetupCommand: z.string(),
  imageVerifyScript: z.string(),
  cosignPublicKeyPem: z.string().nullable(),
  pinnedImageReference: z.string(),
});

export const CreateAgentEnrollmentBodySchema = z.object({
  advertisedRoutes: z.array(z.string()).optional(),
});

export const CreateAgentEnrollmentResponseSchema = z.object({
  enrollment: InternalNetworkAgentEnrollmentSchema,
  apiKey: z.string(),
  deploymentCommand: z.string(),
  composeYaml: z.string(),
  secretsDeploymentCommand: z.string(),
  secretsComposeYaml: z.string(),
  secretsSetupCommand: z.string(),
  imageVerifyScript: z.string(),
  cosignPublicKeyPem: z.string().nullable(),
  pinnedImageReference: z.string(),
  deploymentRequirements: InternalNetworkDeploymentRequirementsSchema,
  tunnelEnrollmentId: z.string().optional(),
  advertisedRoutes: z.array(z.string()).optional(),
});

export const AgentHeartbeatBodySchema = z.object({
  version: z.string().optional(),
  imageDigest: z.string().optional(),
  capabilities: InputJsonValueSchema.optional(),
});

export const InternalDiscoveryRunSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  status: InternalDiscoveryRunStatusSchema,
  remoteTaskId: z.number().nullable().optional(),
  bloodhoundIngestJobId: z.number().nullable().optional(),
  stagedProgress: JsonValueSchema.nullable().optional(),
  failureReason: z.string().nullable().optional(),
  complianceAuthorization:
    InternalDiscoveryComplianceAuthorizationSchema.nullable().optional(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const InternalNetworkGraphDataExportSchema = z.object({
  networkId: z.string(),
  exportedAt: z.iso.datetime(),
  entityCount: z.int().nonnegative(),
  relationshipCount: z.int().nonnegative(),
  entities: z.array(
    z.object({
      entityKind: InternalNetworkAdEntityKindSchema,
      objectId: z.string(),
      displayName: z.string(),
      distinguishedName: z.string().nullable().optional(),
      samAccountName: z.string().nullable().optional(),
      isCrownJewel: z.boolean(),
      isOwned: z.boolean(),
      discoveryRunId: z.string().nullable().optional(),
    }),
  ),
  relationships: z.array(
    z.object({
      sourceObjectId: z.string(),
      targetObjectId: z.string(),
      relationshipType: z.string(),
      isAbusable: z.boolean(),
      discoveryRunId: z.string().nullable().optional(),
    }),
  ),
});

export const InternalNetworkGraphDataDeletionResultSchema = z.object({
  networkId: z.string(),
  deletedEntityCount: z.int().nonnegative(),
  deletedRelationshipCount: z.int().nonnegative(),
  graphCleared: z.boolean(),
});

export const InternalNetworkDataDeletionResultSchema =
  InternalNetworkGraphDataDeletionResultSchema.extend({
    deletedArtifactCount: z.int().nonnegative(),
  });

export const InternalNetworkArtifactDeletionResultSchema = z.object({
  networkId: z.string(),
  deletedArtifactCount: z.int().nonnegative(),
});

export const InternalNetworkDiscoveryDataResetResultSchema = z.object({
  networkId: z.string(),
  deleted: z.object({
    signals: z.int().nonnegative(),
    crownJewels: z.int().nonnegative(),
    ownedPrincipals: z.int().nonnegative(),
    discoveryRuns: z.int().nonnegative(),
    adEntityIndexEntries: z.int().nonnegative(),
    operations: z.int().nonnegative(),
    findings: z.int().nonnegative(),
    assets: z.int().nonnegative(),
    attackPathEvents: z.int().nonnegative(),
    executions: z.int().nonnegative(),
    traces: z.int().nonnegative(),
    artifacts: z.int().nonnegative(),
    runStoreDocuments: z.int().nonnegative(),
    passwordAuditJobs: z.int().nonnegative(),
    checkpoints: z.int().nonnegative(),
  }),
  credentialsVerificationCleared: z.int().nonnegative(),
  bloodhoundGraphCleared: z.boolean(),
});

export const InternalOperationTaskSchema = z.object({
  id: z.string(),
  operationId: z.string(),
  networkId: z.string(),
  command: z.string(),
  remoteTaskId: z.number().nullable(),
  status: InternalNetworkOperationTaskStatusSchema,
  outputPreview: z.string().nullable(),
  failureReason: z.string().nullable(),
  edrOutcome: InternalNetworkEdrOutcomeSchema,
  mitreTechniqueId: z.string().nullable(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const InternalOperationTunnelEngineMetadataSchema = z.object({
  namespace: z.string(),
  podName: z.string(),
  engineTag: z.string(),
  accessKeyId: z.string().optional(),
  secretName: z.string().optional(),
  c2AgentCallbackId: z.string().optional(),
  tunnelEnrollmentId: z.string().optional(),
  engineKind: z.enum(['poseidon', 'terra']).optional(),
});

export const InternalOperationMetadataSchema = z.object({
  executionId: z.string().optional(),
  enrollmentId: z.string().optional(),
  command: z.string().optional(),
  connectionMode: z.enum(['tunnel', 'sensor']).optional(),
  remoteTaskId: z.number().optional(),
  operationTaskId: z.string().optional(),
  summary: z.string().optional(),
  evidenceId: z.string().optional(),
  storageKey: z.string().optional(),
  successIndicator: z.string().optional(),
  tunnelEngine: InternalOperationTunnelEngineMetadataSchema.optional(),
  discoveryBatchOutputs: z
    .array(
      z.object({
        format: z.literal('ce-zip'),
        fileName: z.string().min(1).max(255),
        data: z.string().min(1),
      }),
    )
    .optional(),
  discoveryBatchParams: z
    .array(
      z.record(
        z.string(),
        z.union([
          z.string(),
          z.number(),
          z.boolean(),
          z.null(),
          z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
        ]),
      ),
    )
    .optional(),
});

export const InternalEvidenceEncryptedMetadataSchema = z.object({
  ciphertext: z.string().min(1),
});

export const InternalOperationSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  signalId: z.string().nullable(),
  scenarioId: z.string().nullable(),
  status: InternalNetworkOperationStatusSchema,
  executionTier: InternalNetworkExecutionTierSchema,
  command: z.string().nullable(),
  summary: z.string().nullable(),
  enrollmentId: z.string().nullable(),
  executionId: z.string().nullable().optional(),
  failureReason: z.string().nullable(),
  stoppedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  tasks: z.array(InternalOperationTaskSchema),
  evidenceCount: z.int().nonnegative(),
  evidenceIds: z.array(z.string()).optional().default([]),
});

export const InternalNetworkOperationStatusCountsSchema = z.object({
  PENDING: z.number(),
  RUNNING: z.number(),
  COMPLETED: z.number(),
  FAILED: z.number(),
  STOPPED: z.number(),
});

export type InternalNetworkOperationStatusCountsDto = z.infer<
  typeof InternalNetworkOperationStatusCountsSchema
>;

export const INTERNAL_NETWORK_OPERATION_STATUS_ORDER = [
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'STOPPED',
] as const satisfies readonly z.infer<
  typeof InternalNetworkOperationStatusSchema
>[];

export const EMPTY_INTERNAL_NETWORK_OPERATION_STATUS_COUNTS: InternalNetworkOperationStatusCountsDto =
  {
    PENDING: 0,
    RUNNING: 0,
    COMPLETED: 0,
    FAILED: 0,
    STOPPED: 0,
  };

export const InternalNetworkOperationListQuerySchema =
  PaginationQueryParamsSchema.extend({
    status: z.array(InternalNetworkOperationStatusSchema).optional(),
    command: z.array(z.string().min(1)).optional(),
    executionTier: z.array(InternalNetworkExecutionTierSchema).optional(),
    search: z.string().trim().optional(),
    signalId: z.string().optional(),
    executionId: z.string().optional(),
  });

export const InternalNetworkOperationListResponseSchema = z.object({
  data: z.array(InternalOperationSchema),
  total: z.number(),
  statusCounts: InternalNetworkOperationStatusCountsSchema,
});

export type InternalNetworkOperationListQueryDto = z.infer<
  typeof InternalNetworkOperationListQuerySchema
>;
export type InternalNetworkOperationListResponseDto = z.infer<
  typeof InternalNetworkOperationListResponseSchema
>;

export const InternalCrownJewelSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string(),
  label: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateInternalCrownJewelSchema = z.object({
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string().min(1),
  label: z.string().optional(),
});

export const InternalOwnedPrincipalSourceSchema = z.enum([
  'ONBOARDING',
  'CREDENTIAL_VERIFY',
  'ATTACK_RESULT',
  'MANUAL',
]);

export const InternalOwnedPrincipalSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string(),
  label: z.string().nullable(),
  source: InternalOwnedPrincipalSourceSchema,
  credentialId: z.string().nullable(),
  ownedAt: z.iso.datetime(),
  revokedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateInternalOwnedPrincipalSchema = z.object({
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string().min(1),
  label: z.string().optional(),
  source: InternalOwnedPrincipalSourceSchema.optional(),
  credentialId: z.string().optional(),
});

export const InternalOwnedPrincipalListResponseSchema = z.object({
  data: z.array(InternalOwnedPrincipalSchema),
  total: z.number(),
});

export const ScenarioGuardrailPrecheckResultSchema = z.object({
  allowed: z.boolean(),
  blockedReason: z.string().optional(),
  checkedAt: z.string(),
});

export const ScenarioTypedTaskPlanStepSchema = z.object({
  action: z.string(),
  description: z.string(),
  technique: z.string().optional(),
  command: z.string().optional(),
  expectedOutput: z.string().optional(),
  prerequisites: z.string().optional(),
  sourceHost: z.string().optional(),
  targetHost: z.string().optional(),
  edgeType: z.string().optional(),
});

export const ScenarioTypedTaskPlanSchema = z.object({
  signalCategory: SignalCategorySchema,
  steps: z.array(ScenarioTypedTaskPlanStepSchema),
});

export const TechniqueAuthParamsSchema = z.object({
  domain_name: z.string().min(1).optional(),
  dc_ips: z.string().min(1).optional(),
  dns_server_ips: z.string().optional(),
  username: z.string().min(1).optional(),
  password: z.string().min(1).optional(),
  ntlm_hash: z.string().min(1).optional(),
  auth_method: z.enum(['AUTO', 'NTLM', 'KERBEROS']).optional(),
});

export const TechniqueCommandOutputSchema = z.object({
  command: z.string(),
  status: z.enum(['executed', 'error']),
  stdout: z.string(),
  stderr: z.string().optional(),
  exit_code: z.int().optional(),
});

export const KerberoastRequestParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  domain_name: z.string().min(1),
  target_object_id: z.string().min(1),
  spn: z.string().optional(),
});

export const AsrepRoastParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  domain_name: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const AclProbeParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  target_object_id: z.string().min(1),
  rights: z.string().optional(),
  execution_tier: InternalNetworkExecutionTierSchema.optional(),
  relationshipType: z.string().optional(),
  signal_category: SignalCategorySchema.optional(),
  action_class: InternalNetworkActionClassSchema.optional(),
  steps: z.array(ScenarioTypedTaskPlanStepSchema).optional(),
});

export const AclAbuseParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  target_object_id: z.string().min(1),
  rights: z.string().optional(),
  execution_tier: InternalNetworkExecutionTierSchema.optional(),
  relationshipType: z.string().optional(),
  signal_category: SignalCategorySchema.optional(),
  action_class: InternalNetworkActionClassSchema.optional(),
  steps: z.array(ScenarioTypedTaskPlanStepSchema).optional(),
  hold_state: z.boolean().optional(),
  restore: z.boolean().optional(),
});

export const PassTheHashParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  hostname: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const DcsyncParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  domain_name: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const HashCrackOfflineParamsSchema = z.object({
  storeHashMaterial: z.boolean().optional(),
  jobId: z.string().optional(),
});

export const DelegationAbuseParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  target_object_id: z.string().min(1),
  delegation_type: z
    .enum(['CONSTRAINED', 'UNCONSTRAINED', 'RESOURCE_BASED'])
    .optional(),
});

export const AdcsProbeParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  hostname: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const LateralMoveProbeParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  hostname: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const LapsReadParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  target_object_id: z.string().min(1),
  hostname: z.string().optional(),
});

export const GmsaReadParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  target_object_id: z.string().min(1),
});

export const PsremoteProbeParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  hostname: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const NtlmCoerceParamsSchema = TechniqueAuthParamsSchema.extend({
  scenario_id: z.string().optional(),
  hostname: z.string().min(1),
  target_object_id: z.string().min(1),
});

export const ScenarioEvidencePlanSchema = z.object({
  artifacts: z.array(z.string()),
  commandOutput: z.boolean(),
});

export const EnabledBlockGuardrailSchema = z.object({
  name: z.string(),
  rules: z.array(GuardrailRuleSchema),
});

export const GeneratedScenarioDraftSchema = z.object({
  actionClass: InternalNetworkActionClassSchema,
  executionTier: InternalNetworkExecutionTierSchema,
  pathReference: z.string().nullable(),
  crownJewelId: z.string().nullable(),
  blastRadiusSummary: z.string(),
  guardrailPrecheckResult: ScenarioGuardrailPrecheckResultSchema,
  typedTaskPlan: ScenarioTypedTaskPlanSchema,
  evidencePlan: ScenarioEvidencePlanSchema,
  status: ScenarioStatusSchema,
});

export const ScenarioSchema = z.object({
  id: z.string(),
  signalId: z.string(),
  pathReference: z.string().nullable(),
  crownJewelId: z.string().nullable(),
  actionClass: InternalNetworkActionClassSchema,
  executionTier: InternalNetworkExecutionTierSchema,
  guardrailPrecheckResult: ScenarioGuardrailPrecheckResultSchema.nullable(),
  blastRadiusSummary: z.string().nullable(),
  evidencePlan: ScenarioEvidencePlanSchema.nullable(),
  typedTaskPlan: ScenarioTypedTaskPlanSchema.nullable(),
  status: ScenarioStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ScenarioListResponseSchema = z.object({
  data: z.array(ScenarioSchema),
  total: z.number(),
});

export {
  ABUSABLE_AD_RELATIONSHIP_TYPES,
  AdGraphCrownJewelNodePropertiesSchema,
  AdGraphEdgeSchema,
  AdGraphEntityViewSchema,
  AdGraphKnownNodePropertiesSchema,
  AdGraphLinkSchema,
  AdGraphNodeSchema,
  AdGraphQuerySchema,
  AdGraphResponseSchema,
  AdGraphNetworkNodePropertiesSchema,
  InternalNetworkAdRelationshipTypeSchema,
  type AbusableAdRelationshipType,
  type AdGraphCrownJewelNodePropertiesDto,
  type AdGraphEdgeDto,
  type AdGraphEntityViewDto,
  type AdGraphKnownNodePropertiesDto,
  type AdGraphLinkDto,
  type AdGraphNodeDto,
  type AdGraphQueryDto,
  type AdGraphResponseDto,
  type AdGraphNetworkNodePropertiesDto,
} from './ad-graph.schema';

export const DiscoveryCollectionZipOutputSchema = z.object({
  format: z.literal('ce-zip'),
  fileName: z.string().min(1).max(255),
  data: z
    .string()
    .min(1)
    .max(52_428_800)
    .refine(
      (value) => /^[A-Za-z0-9+/]+={0,2}$/.test(value) && value.length % 4 === 0,
      'Invalid base64-encoded BloodHound export data',
    ),
});

export const CredentialVerifyOutputSchema = z.object({
  success: z.literal(true),
});

export const RemoteTaskErrorSchema = z.object({
  code: z.string().min(1).max(100),
  message: z.string().min(1).max(2_000),
  details: JsonValueSchema.optional(),
});

export const RemoteTaskSuccessOutputSchema = z.object({
  status: z.literal('success'),
  data: z.unknown(),
});

export const RemoteTaskErrorOutputSchema = z.object({
  status: z.literal('error'),
  error: RemoteTaskErrorSchema,
});

export const RemoteTaskOutputSchema = z.discriminatedUnion('status', [
  RemoteTaskSuccessOutputSchema,
  RemoteTaskErrorOutputSchema,
]);

export const SubmitDiscoveryResultsResponseSchema = z.object({
  entitiesUpserted: z.number(),
  relationshipsUpserted: z.number(),
  bloodhoundIngestJobId: z.int().positive().optional(),
});

export const ExternalNetworkFullReportBodySchema = z.object({
  findingIds: z.array(z.string()).min(1),
  title: z.string().min(1).optional(),
});

export const ExternalNetworkFullReportResponseSchema = z.object({
  reportId: z.string(),
  title: z.string(),
  findingIds: z.array(z.string()),
});

export const GenerateInternalNetworkSignalsResponseSchema = z.object({
  queued: z.boolean(),
  discoveryRunId: z.string(),
});

export const ImportBloodhoundExportBodySchema =
  DiscoveryCollectionZipOutputSchema;

export const ImportBloodhoundExportResponseSchema =
  SubmitDiscoveryResultsResponseSchema.extend({
    discoveryRunId: z.string(),
  });

export const IngestInternalIntegrationSignalBodySchema = z.object({
  provider: z.string().min(1),
  externalId: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  reason: z.string().min(1),
  category: SignalCategorySchema,
  severity: z
    .enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'])
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const IngestInternalIntegrationSignalResponseSchema = z.object({
  signalId: z.string().nullable(),
  isNew: z.boolean(),
});

export const RunInternalNetworkSignalAttackResponseSchema = z.object({
  scenarioId: z.string(),
  status: ScenarioStatusSchema,
});

export const RunInternalNetworkC2CommandBodySchema = z.object({
  command: z.enum(ALLOWED_REMOTE_EXECUTION_COMMANDS),
  params: z.record(z.string(), z.unknown()).optional(),
  preferredHost: z.string().min(1).optional(),
  executionId: z.uuid().optional(),
  scenarioId: z.string().min(1).optional(),
});

export const RunInternalNetworkC2CommandResponseSchema = z.object({
  operationId: z.string(),
  executionId: z.string(),
  status: InternalNetworkOperationStatusSchema,
});

export type RunInternalNetworkC2CommandBodyDto = z.infer<
  typeof RunInternalNetworkC2CommandBodySchema
>;
export type RunInternalNetworkC2CommandResponseDto = z.infer<
  typeof RunInternalNetworkC2CommandResponseSchema
>;

export const HostAgentArtifactHashSchema = z.object({
  payloadUuid: z.string(),
  filename: z.string(),
  sha256: z.string().nullable(),
});

export const InternalNetworkAgentArtifactsResponseSchema = z.object({
  apollo: HostAgentArtifactHashSchema.optional(),
  poseidon: HostAgentArtifactHashSchema.optional(),
  engine: HostAgentArtifactHashSchema.optional(),
});

export const InternalNetworkEdrExclusionTemplateSchema = z.object({
  hashes: z.array(z.string()),
  processNames: z.array(z.string()),
  paths: z.array(z.string()),
});

export const InternalNetworkOnboardingPackageSchema = z.object({
  networkId: z.string(),
  sensor: z.object({
    imageDigest: z.string().nullable(),
    processNames: z.array(z.string()),
    healthPort: z.int(),
  }),
  hostAgents: InternalNetworkAgentArtifactsResponseSchema,
  networkIndicators: z.object({
    c2CallbackUrl: z.string(),
    smbPipeName: z.string(),
    tcpP2pPort: z.int(),
    c2PollIntervalSeconds: z.int(),
    heartbeatIntervalMs: z.int(),
  }),
  edrTemplates: z.object({
    windowsDefender: InternalNetworkEdrExclusionTemplateSchema,
    crowdStrike: InternalNetworkEdrExclusionTemplateSchema,
    sentinelOne: InternalNetworkEdrExclusionTemplateSchema,
  }),
});

export type InternalNetworkAgentArtifactsResponseDto = z.infer<
  typeof InternalNetworkAgentArtifactsResponseSchema
>;
export type InternalNetworkOnboardingPackageDto = z.infer<
  typeof InternalNetworkOnboardingPackageSchema
>;

export type InternalNetworkDto = z.infer<typeof InternalNetworkSchema>;
export type CreateInternalNetworkDto = z.infer<
  typeof CreateInternalNetworkSchema
>;
export type UpdateInternalNetworkDto = z.infer<
  typeof UpdateInternalNetworkSchema
>;
export type InternalNetworkListQueryDto = z.infer<
  typeof InternalNetworkListQuerySchema
>;
export type InternalNetworkAgentEnrollmentDto = z.infer<
  typeof InternalNetworkAgentEnrollmentSchema
>;
export type InternalNetworkRemoteExecutionStatusDto = z.infer<
  typeof InternalNetworkRemoteExecutionStatusSchema
>;
export type InternalNetworkSensorC2PhaseDto = z.infer<
  typeof InternalNetworkSensorC2PhaseSchema
>;
export type InternalNetworkDiscoveryBlockReasonDto = z.infer<
  typeof InternalNetworkDiscoveryBlockReasonSchema
>;
export type InternalNetworkDiscoveryReadinessDto = z.infer<
  typeof InternalNetworkDiscoveryReadinessSchema
>;
export type InternalNetworkDeploymentStatusDto =
  InternalNetworkAgentEnrollmentDto['deploymentStatus'];
export type InternalNetworkSensorReleaseDto = z.infer<
  typeof InternalNetworkSensorReleaseSchema
>;
export type CreateInternalNetworkSensorReleaseDto = z.infer<
  typeof CreateInternalNetworkSensorReleaseSchema
>;
export type SensorReleaseDownloadBundleDto = z.infer<
  typeof SensorReleaseDownloadBundleSchema
>;
export type RotateEnrollmentKeyResponseDto = z.infer<
  typeof RotateEnrollmentKeyResponseSchema
>;
export type AgentHeartbeatResponseDto = z.infer<
  typeof AgentHeartbeatResponseSchema
>;
export type CreateOperationEvidenceUploadDto = z.infer<
  typeof CreateOperationEvidenceUploadSchema
>;
export type OperationEvidenceUploadResponseDto = z.infer<
  typeof OperationEvidenceUploadResponseSchema
>;
export type ConfirmOperationEvidenceUploadDto = z.infer<
  typeof ConfirmOperationEvidenceUploadSchema
>;
export type InternalEvidenceDto = z.infer<typeof InternalEvidenceSchema>;
export type InternalEvidenceContentDto = z.infer<
  typeof InternalEvidenceContentSchema
>;
export type RemoteDiscoveryCollectParamsDto = z.infer<
  typeof RemoteDiscoveryCollectParamsSchema
>;
export type RemoteCredentialVerifyParamsDto = z.infer<
  typeof RemoteCredentialVerifyParamsSchema
>;
export type CredentialVerifyOutputDto = z.infer<
  typeof CredentialVerifyOutputSchema
>;
export type RemoteTaskErrorDto = z.infer<typeof RemoteTaskErrorSchema>;
export type RemoteTaskOutputDto = z.infer<typeof RemoteTaskOutputSchema>;
export type InternalNetworkDeploymentRequirementsDto = z.infer<
  typeof InternalNetworkDeploymentRequirementsSchema
>;
export type CreateAgentEnrollmentBodyDto = z.infer<
  typeof CreateAgentEnrollmentBodySchema
>;
export type CreateAgentEnrollmentResponseDto = z.infer<
  typeof CreateAgentEnrollmentResponseSchema
>;
export type InternalDiscoveryRunDto = z.infer<
  typeof InternalDiscoveryRunSchema
>;
export const PatchInternalNetworkOperationTaskEdrOutcomeSchema = z.object({
  edrOutcome: InternalNetworkEdrOutcomeSchema,
});

export const InternalNetworkOperationTaskStatusCountSchema = z.object({
  PENDING: z.number().int().nonnegative(),
  DISPATCHED: z.number().int().nonnegative(),
  RUNNING: z.number().int().nonnegative(),
  COMPLETED: z.number().int().nonnegative(),
  FAILED: z.number().int().nonnegative(),
  STOPPED: z.number().int().nonnegative(),
  CANCELLED: z.number().int().nonnegative(),
});

const InternalNetworkEdrOutcomeCountSchema = z.object({
  BLOCKED: z.number().int().nonnegative(),
  ALERTED: z.number().int().nonnegative(),
  PERMITTED: z.number().int().nonnegative(),
  UNKNOWN: z.number().int().nonnegative(),
});

export const InternalNetworkEdrEffectivenessSchema = z.object({
  networkId: z.string(),
  totals: InternalNetworkEdrOutcomeCountSchema.extend({
    byStatus: InternalNetworkOperationTaskStatusCountSchema,
  }),
  byTechnique: z.array(
    InternalNetworkEdrOutcomeCountSchema.extend({
      command: z.string(),
      mitreTechniqueId: z.string().nullable(),
      byStatus: InternalNetworkOperationTaskStatusCountSchema,
    }),
  ),
  byHost: z
    .array(
      InternalNetworkEdrOutcomeCountSchema.extend({
        hostname: z.string(),
      }),
    )
    .default([]),
});

export type InternalOperationTaskDto = z.infer<
  typeof InternalOperationTaskSchema
>;
export type InternalNetworkEdrOutcomeDto = z.infer<
  typeof InternalNetworkEdrOutcomeSchema
>;
export type InternalNetworkEdrEffectivenessDto = z.infer<
  typeof InternalNetworkEdrEffectivenessSchema
>;
export type PatchInternalNetworkOperationTaskEdrOutcomeDto = z.infer<
  typeof PatchInternalNetworkOperationTaskEdrOutcomeSchema
>;
export type InternalOperationDto = z.infer<typeof InternalOperationSchema>;
export type InternalOperationMetadataDto = z.infer<
  typeof InternalOperationMetadataSchema
>;
export type InternalEvidenceEncryptedMetadataDto = z.infer<
  typeof InternalEvidenceEncryptedMetadataSchema
>;
export type InternalCrownJewelDto = z.infer<typeof InternalCrownJewelSchema>;
export type InternalNetworkCrownJewelSuggestionDto = z.infer<
  typeof InternalNetworkCrownJewelSuggestionSchema
>;
export type InternalNetworkCrownJewelSuggestionsResponseDto = z.infer<
  typeof InternalNetworkCrownJewelSuggestionsResponseSchema
>;
export type InternalOwnedPrincipalDto = z.infer<
  typeof InternalOwnedPrincipalSchema
>;
export type InternalOwnedPrincipalSourceDto = z.infer<
  typeof InternalOwnedPrincipalSourceSchema
>;
export type ScenarioDto = z.infer<typeof ScenarioSchema>;
export type ScenarioGuardrailPrecheckResultDto = z.infer<
  typeof ScenarioGuardrailPrecheckResultSchema
>;
export type ScenarioTypedTaskPlanDto = z.infer<
  typeof ScenarioTypedTaskPlanSchema
>;
export type KerberoastRequestParamsDto = z.infer<
  typeof KerberoastRequestParamsSchema
>;
export type TechniqueCommandOutputDto = z.infer<
  typeof TechniqueCommandOutputSchema
>;
export type AsrepRoastParamsDto = z.infer<typeof AsrepRoastParamsSchema>;
export type AclProbeParamsDto = z.infer<typeof AclProbeParamsSchema>;
export type AclAbuseParamsDto = z.infer<typeof AclAbuseParamsSchema>;
export type PassTheHashParamsDto = z.infer<typeof PassTheHashParamsSchema>;
export type DcsyncParamsDto = z.infer<typeof DcsyncParamsSchema>;
export type DelegationAbuseParamsDto = z.infer<
  typeof DelegationAbuseParamsSchema
>;
export type AdcsProbeParamsDto = z.infer<typeof AdcsProbeParamsSchema>;
export type LateralMoveProbeParamsDto = z.infer<
  typeof LateralMoveProbeParamsSchema
>;
export type LapsReadParamsDto = z.infer<typeof LapsReadParamsSchema>;
export type GmsaReadParamsDto = z.infer<typeof GmsaReadParamsSchema>;
export type PsremoteProbeParamsDto = z.infer<typeof PsremoteProbeParamsSchema>;
export type NtlmCoerceParamsDto = z.infer<typeof NtlmCoerceParamsSchema>;
export type ScenarioEvidencePlanDto = z.infer<
  typeof ScenarioEvidencePlanSchema
>;
export type EnabledBlockGuardrailDto = z.infer<
  typeof EnabledBlockGuardrailSchema
>;
export type GeneratedScenarioDraftDto = z.infer<
  typeof GeneratedScenarioDraftSchema
>;
export type DiscoveryCollectionZipOutputDto = z.infer<
  typeof DiscoveryCollectionZipOutputSchema
>;
export type SubmitDiscoveryResultsResponseDto = z.infer<
  typeof SubmitDiscoveryResultsResponseSchema
>;
export type ExternalNetworkFullReportBodyDto = z.infer<
  typeof ExternalNetworkFullReportBodySchema
>;
export type ExternalNetworkFullReportResponseDto = z.infer<
  typeof ExternalNetworkFullReportResponseSchema
>;
export type GenerateInternalNetworkSignalsResponseDto = z.infer<
  typeof GenerateInternalNetworkSignalsResponseSchema
>;
export type ImportBloodhoundExportBodyDto = z.infer<
  typeof ImportBloodhoundExportBodySchema
>;
export type ImportBloodhoundExportResponseDto = z.infer<
  typeof ImportBloodhoundExportResponseSchema
>;
export type IngestInternalIntegrationSignalBodyDto = z.infer<
  typeof IngestInternalIntegrationSignalBodySchema
>;
export type IngestInternalIntegrationSignalResponseDto = z.infer<
  typeof IngestInternalIntegrationSignalResponseSchema
>;
export type AgentHeartbeatBodyDto = z.infer<typeof AgentHeartbeatBodySchema>;
export type CreateInternalCrownJewelDto = z.infer<
  typeof CreateInternalCrownJewelSchema
>;
export type CreateInternalOwnedPrincipalDto = z.infer<
  typeof CreateInternalOwnedPrincipalSchema
>;

const SURFACE_SIGNAL_CONFIDENCE_FILTER_VALUES = [
  'HIGHEST',
  'HIGH',
  'MEDIUM',
  'LOW',
  'LOWEST',
  'ALL',
] as const;

const NetworkSignalConfidenceFilterSchema =
  createConfidenceFilterQueryParamSchema(
    SURFACE_SIGNAL_CONFIDENCE_FILTER_VALUES,
  );

const NetworkSignalCategoryQuerySchema = OptionalStringArrayQueryParamSchema;

export const InternalNetworkSignalListQuerySchema =
  PaginationQueryParamsSchema.extend({
    category: NetworkSignalCategoryQuerySchema,
    search: z
      .string()
      .optional()
      .describe('Case-insensitive contains filter on signal description'),
    entityKind: InternalNetworkAdEntityKindSchema.optional(),
    targetId: z.string().optional(),
    signalId: z.string().optional(),
    assessmentId: z.string().optional(),
    stage: z.array(SignalStageSchema).optional(),
    status: z.array(SignalStatusSchema).optional(),
    confirmedVulnerabilityFindingState: z
      .array(ConfirmedVulnerabilityFindingStateSchema)
      .optional(),
    confidence: NetworkSignalConfidenceFilterSchema,
    origin: z.array(SignalOriginSchema).optional(),
    assigneeId: z.union([z.string(), z.array(z.string())]).optional(),
    hasExecution: OptionalBooleanQueryParamSchema,
    hasFinding: OptionalBooleanQueryParamSchema,
    hasSecurityTestCase: OptionalBooleanQueryParamSchema,
    importBatchId: OptionalStringArrayQueryParamSchema.describe(
      'Filter signals linked to one or more import batches',
    ),
    cweIds: z.array(z.string()).optional(),
    owaspCategory: z.array(z.string()).optional(),
    createdStartDate: z.coerce.date().optional(),
    createdEndDate: z.coerce.date().optional(),
    testedStartDate: z.coerce.date().optional(),
    testedEndDate: z.coerce.date().optional(),
    rejectedOnly: OptionalBooleanQueryParamSchema,
    includeRejected: OptionalBooleanQueryParamSchema,
    isActive: OptionalBooleanQueryParamSchema,
    lifecycleState: LifecycleStateFilterSchema.optional().describe(
      'Filter by asset lifecycle state (active, archived, or all)',
    ),
  });

export type InternalNetworkSignalListQueryDto = z.infer<
  typeof InternalNetworkSignalListQuerySchema
>;

export const InternalNetworkSignalsCountQuerySchema =
  InternalNetworkSignalListQuerySchema.omit({
    skip: true,
    limit: true,
  });

export const InternalNetworkSignalsCountResponseSchema = z.object({
  count: z.number(),
});

export const InternalNetworkSignalEntityLinkRoleSchema = z.enum([
  'PRIMARY',
  'SOURCE',
  'TARGET',
  'PATH_NODE',
  'CROWN_JEWEL',
]);

export const InternalNetworkSignalLinkedEntitySchema = z.object({
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string(),
  role: InternalNetworkSignalEntityLinkRoleSchema,
  relationshipType: z.string().nullable().optional(),
  pathIndex: z.int().nullable().optional(),
  displayName: z.string().nullable().optional(),
  nodeId: z.string().nullable().optional(),
});

export const InternalNetworkSignalSchema = z.object({
  id: z.string(),
  category: SignalCategorySchema,
  origin: SignalOriginSchema,
  stage: SignalStageSchema,
  status: SignalStatusSchema.nullable(),
  description: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  cweIds: z.array(z.string()).default([]),
  owaspCategory: z.string().nullable().optional(),
  isActive: z.boolean(),
  targetId: z.string(),
  targetEntityId: z.string().nullable().optional(),
  targetEntityKind: InternalNetworkAdEntityKindSchema.nullable().optional(),
  targetSummary: z.string().nullable().optional(),
  assigneeId: z.string().nullable().optional(),
  lastSmokeTestedAt: z.coerce.date().nullable().optional(),
  validationRejected: z.boolean().nullable().optional(),
  securityTestCaseId: z.string().nullable().optional(),
  smokeTestSummary: z.string().nullable().optional(),
  linkedEntities: z.array(InternalNetworkSignalLinkedEntitySchema).optional(),
  linkedFinding: SignalLinkedFindingPreviewSchema.nullish(),
  createdAt: z.coerce.date(),
});

export const InternalNetworkSignalListResponseSchema = z.object({
  data: z.array(InternalNetworkSignalSchema),
  total: z.number(),
});

export type InternalNetworkSignalDto = z.infer<
  typeof InternalNetworkSignalSchema
>;
export type InternalNetworkSignalLinkedEntityDto = z.infer<
  typeof InternalNetworkSignalLinkedEntitySchema
>;
export type InternalNetworkSignalListResponseDto = z.infer<
  typeof InternalNetworkSignalListResponseSchema
>;

export {
  SurfaceFindingsListQuerySchema,
  SurfaceFindingsListResponseSchema,
  SurfaceFindingsCountQuerySchema,
  SurfaceFindingsCountResponseSchema,
  ExternalNetworkSignalListResponseSchema,
};

export const GetInternalNetworkAdEntityResponseSchema = z.object({
  entity: InternalNetworkAdEntityDetailSchema.nullable(),
});

export const GetInternalNetworkAdEntityRelationshipsQuerySchema = z.object({
  direction: z.enum(['outgoing', 'incoming', 'both']).optional(),
  limit: z.coerce.number().min(1).max(200).optional(),
});

export const InternalNetworkAdEntityRelationshipSchema = z.object({
  relationshipType: z.string(),
  sourceNodeId: z.string(),
  targetNodeId: z.string(),
  isAbusable: z.boolean(),
  direction: z.enum(['outgoing', 'incoming']),
  peerNodeId: z.string(),
  peerDisplayName: z.string().nullable(),
  peerEntityKind: InternalNetworkAdEntityKindSchema.nullable(),
});

export const GetInternalNetworkAdEntityRelationshipsResponseSchema = z.object({
  relationships: z.array(InternalNetworkAdEntityRelationshipSchema),
  total: z.number(),
  centerNodeId: z.string().nullable().optional(),
});

export const InternalNetworkCrownJewelSuggestionSchema = z.object({
  entityKind: InternalNetworkAdEntityKindSchema,
  entityId: z.string(),
  displayName: z.string(),
  samAccountName: z.string().nullable(),
  suggestionReason: z.string(),
  defaultSelected: z.boolean(),
});

export const InternalNetworkCrownJewelSuggestionsResponseSchema = z.object({
  suggestions: z.array(InternalNetworkCrownJewelSuggestionSchema),
});

export const InternalNetworkFindingsCountsBySeveritySchema = z.object({
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
  info: z.number(),
});

export const InternalNetworkStatsSchema = z.object({
  signalCount: z.number(),
  openFindingCount: z.number(),
  findingsCountsBySeverity: InternalNetworkFindingsCountsBySeveritySchema,
  crownJewelCount: z.number(),
  ownedPrincipalCount: z.number(),
  onlineEnrollmentCount: z.number(),
  latestDiscoveryStatus: InternalDiscoveryRunStatusSchema.nullable(),
  totalCanonicalAssets: z.number().default(0),
  totalUsers: z.number(),
  totalGroups: z.number(),
  totalComputers: z.number(),
  totalOus: z.number(),
  totalDomains: z.number(),
  totalGpos: z.number(),
  totalDomainControllers: z.number(),
  totalCertTemplates: z.number(),
  totalContainers: z.number(),
  findingsByInfraImpact: InfraImpactCountSchema.default(
    EMPTY_INFRA_IMPACT_COUNTS,
  ),
  findingsByBusinessRisk: BusinessRiskCountSchema.default(
    EMPTY_BUSINESS_RISK_COUNTS,
  ),
  credentialExposureCount: z.number().default(0),
  compromisedHostCount: z.number().default(0),
  weaknessCounts: z.array(WeaknessCountRowSchema).default([]),
  threatActorFlows: z.array(ThreatActorFlowRowSchema).default([]),
});

export const InternalNetworkServiceCountSchema = z.object({
  service: z.string(),
  count: z.int().nonnegative(),
});

export const InternalNetworkProductCountSchema = z.object({
  product: z.string(),
  version: z.string().nullable(),
  count: z.int().nonnegative(),
});

export const InternalNetworkPortCountSchema = z.object({
  protocol: z.string(),
  port: z.int().nonnegative(),
  count: z.int().nonnegative(),
});

export const InternalNetworkAssetKindCountSchema = z.object({
  assetKind: z.enum([
    'UNKNOWN',
    'COMPUTER',
    'SERVER',
    'DOMAIN_CONTROLLER',
    'NETWORK_DEVICE',
    'APPLIANCE',
  ]),
  count: z.int().nonnegative(),
});

export const InternalNetworkSegmentSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  cidrs: z.array(z.string()),
  source: z.enum(['TUNNEL_ROUTE', 'NMAP', 'MANUAL']),
});

export const InternalNetworkVendorCountSchema = z.object({
  vendor: z.string(),
  count: z.int().nonnegative(),
});

export const InternalNetworkAdEntityKindCountSchema = z.object({
  entityKind: InternalNetworkAdEntityKindSchema,
  count: z.int().nonnegative(),
});

export const InternalNetworkTechnologiesSummarySchema = z.object({
  totalAssetsWithServices: z.int().nonnegative(),
  totalServices: z.int().nonnegative(),
  totalProducts: z.int().nonnegative(),
  segmentCount: z.int().nonnegative(),
  topServices: z.array(InternalNetworkServiceCountSchema),
  topProducts: z.array(InternalNetworkProductCountSchema),
  topPorts: z.array(InternalNetworkPortCountSchema),
  segments: z.array(InternalNetworkSegmentSummarySchema),
  assetKinds: z.array(InternalNetworkAssetKindCountSchema),
  adEntityKinds: z.array(InternalNetworkAdEntityKindCountSchema),
  topVendors: z.array(InternalNetworkVendorCountSchema),
  domainNames: z.array(z.string()),
  gpoNames: z.array(z.string()),
  certTemplateNames: z.array(z.string()),
  certTemplateCount: z.int().nonnegative(),
  domainControllerNames: z.array(z.string()),
  domainControllerCount: z.int().nonnegative(),
});

export const InternalNetworkSignalTrendQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).optional().prefault(30),
});

export const InternalNetworkSignalTrendDaySchema = z.object({
  date: z.string(),
  count: z.int().nonnegative(),
});

export const InternalNetworkSignalTrendResponseSchema = z.object({
  dailyData: z.array(InternalNetworkSignalTrendDaySchema),
  totalSignals: z.int().nonnegative(),
});

export const InternalNetworkCredentialTypeSchema = z.enum([
  'DOMAIN_USER',
  'SERVICE_ACCOUNT',
  'NTLM_HASH',
  'AZURE_REFRESH_TOKEN',
]);

export const InternalNetworkCredentialAuthMethodSchema = z.enum([
  'AUTO',
  'NTLM',
  'KERBEROS',
]);

export const InternalNetworkCredentialSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  name: z.string(),
  username: z.string(),
  password: z.string().nullable(),
  ntlmHash: z.string().nullable(),
  domain: z.string().nullable(),
  credentialType: InternalNetworkCredentialTypeSchema,
  authMethod: InternalNetworkCredentialAuthMethodSchema,
  isDisabled: z.boolean(),
  isOwned: z.boolean(),
  lastVerifiedAt: z.iso.datetime().nullable(),
  isHvt: z.boolean().optional().default(false),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const InternalNetworkCredentialListResponseSchema = z.object({
  data: z.array(InternalNetworkCredentialSchema),
  total: z.number(),
});

const CreateInternalNetworkCredentialBaseSchema = z.object({
  name: z.string().min(1),
  username: z.string().min(1),
  password: z.string().optional(),
  ntlmHash: z.string().optional(),
  domain: z.string().optional(),
  credentialType: InternalNetworkCredentialTypeSchema.default('DOMAIN_USER'),
  authMethod: InternalNetworkCredentialAuthMethodSchema.default('NTLM'),
});

export const CreateInternalNetworkCredentialSchema =
  CreateInternalNetworkCredentialBaseSchema.superRefine((value, ctx) => {
    if (value.credentialType === 'NTLM_HASH') {
      if (!value.ntlmHash || value.ntlmHash.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'NTLM hash is required for hash credentials',
          path: ['ntlmHash'],
        });
      }

      return;
    }

    if (value.credentialType === 'AZURE_REFRESH_TOKEN') {
      if (!value.password || value.password.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Refresh token is required for Azure credentials',
          path: ['password'],
        });
      }

      if (!value.domain || value.domain.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Azure tenant ID is required for Azure credentials',
          path: ['domain'],
        });
      }

      return;
    }

    if (!value.password || value.password.trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Password is required for domain credentials',
        path: ['password'],
      });
    }
  });

export const UpdateInternalNetworkCredentialSchema =
  CreateInternalNetworkCredentialBaseSchema.partial().extend({
    isDisabled: z.boolean().optional(),
    isOwned: z.boolean().optional(),
  });

export const VerifyInternalNetworkCredentialResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  error: z.string().optional(),
});

export type InternalNetworkStatsDto = z.infer<
  typeof InternalNetworkStatsSchema
>;
export type InternalNetworkFindingsCountsBySeverityDto = z.infer<
  typeof InternalNetworkFindingsCountsBySeveritySchema
>;
export type InternalNetworkTechnologiesSummaryDto = z.infer<
  typeof InternalNetworkTechnologiesSummarySchema
>;
export type InternalNetworkServiceCountDto = z.infer<
  typeof InternalNetworkServiceCountSchema
>;
export type InternalNetworkProductCountDto = z.infer<
  typeof InternalNetworkProductCountSchema
>;
export type InternalNetworkPortCountDto = z.infer<
  typeof InternalNetworkPortCountSchema
>;
export type InternalNetworkAssetKindCountDto = z.infer<
  typeof InternalNetworkAssetKindCountSchema
>;
export type InternalNetworkSegmentSummaryDto = z.infer<
  typeof InternalNetworkSegmentSummarySchema
>;
export type InternalNetworkVendorCountDto = z.infer<
  typeof InternalNetworkVendorCountSchema
>;
export type InternalNetworkAdEntityKindCountDto = z.infer<
  typeof InternalNetworkAdEntityKindCountSchema
>;
export type InternalNetworkSignalTrendQueryDto = z.infer<
  typeof InternalNetworkSignalTrendQuerySchema
>;
export type InternalNetworkSignalTrendResponseDto = z.infer<
  typeof InternalNetworkSignalTrendResponseSchema
>;
export type InternalNetworkAdEntityRelationshipDto = z.infer<
  typeof InternalNetworkAdEntityRelationshipSchema
>;
export type InternalNetworkCredentialDto = z.infer<
  typeof InternalNetworkCredentialSchema
>;
export type CreateInternalNetworkCredentialDto = z.infer<
  typeof CreateInternalNetworkCredentialSchema
>;
export type UpdateInternalNetworkCredentialDto = z.infer<
  typeof UpdateInternalNetworkCredentialSchema
>;
export type InternalDiscoveryComplianceAuthorizationDto = z.infer<
  typeof InternalDiscoveryComplianceAuthorizationSchema
>;
export type InternalNetworkGraphDataExportDto = z.infer<
  typeof InternalNetworkGraphDataExportSchema
>;
export type InternalNetworkGraphDataDeletionResultDto = z.infer<
  typeof InternalNetworkGraphDataDeletionResultSchema
>;
export type InternalNetworkDataDeletionResultDto = z.infer<
  typeof InternalNetworkDataDeletionResultSchema
>;
export type InternalNetworkArtifactDeletionResultDto = z.infer<
  typeof InternalNetworkArtifactDeletionResultSchema
>;
export type InternalNetworkDiscoveryDataResetResultDto = z.infer<
  typeof InternalNetworkDiscoveryDataResetResultSchema
>;

export const DiscoveryActivityLogEntrySchema = z.object({
  message: z.string(),
  stage: z.string().optional(),
  status: z.string().optional(),
  ts: z.number(),
});

export type DiscoveryActivityLogEntryDto = z.infer<
  typeof DiscoveryActivityLogEntrySchema
>;

export const BloodHoundCypherQueryBodySchema = z.object({
  query: z.string().min(1).max(8000),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const BloodHoundCypherQueryResponseSchema = z.object({
  nodes: z.record(z.string(), z.unknown()).optional(),
  edges: z.array(z.unknown()).optional(),
  literals: z.array(z.unknown()).optional(),
});

export type BloodHoundCypherQueryBodyDto = z.infer<
  typeof BloodHoundCypherQueryBodySchema
>;
export type BloodHoundCypherQueryResponseDto = z.infer<
  typeof BloodHoundCypherQueryResponseSchema
>;
